/* eslint-disable @typescript-eslint/no-explicit-any */
import { Instance } from 'mobx-state-tree';
import { FlowReturn } from 'mobx-state-tree/dist/internal';
import ms from 'ms';
import { useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { UploadOptions } from '.';
import {
  PAYMENT_CHECKER_O1_PERMISSION,
  PAYMENT_CHECKER_O2_PERMISSION,
  PAYMENT_VIEW_PERMISSION,
  STATUS_CODE_NOT_AUTHORIZED,
} from '../../constants';
import useInject from '../../hooks/useInject';
import AuthModel from '../../stores/auth.store';

type HandleChangeFn = (newValue: number) => void;

type UseViewModelReturn = {
  handleChange: HandleChangeFn;
  handleUploadFile: (
    file: File | undefined,
    options: UploadOptions
  ) => Promise<void>;
  hasViewAndCheckerPermission?: string;
  selectTab: number;
  uploaded: boolean;
  failedAlert: string;
  auth: Instance<typeof AuthModel>;
  setLoading: (val: boolean) => void;
  uploadRecurringFile: (
    file: FormData,
    merchantID: string
  ) => Promise<FlowReturn<any>>;
};

const useViewModel = (): UseViewModelReturn => {
  const { t } = useTranslation();
  const { auth, setLoading, uploadRecurringFile } = useInject((root) => root);
  const [selectTab, setSelectTab] = useState<number>(0);
  const [uploaded, setUploaded] = useState<boolean>(false);
  const [failedAlert, setFailedAlert] = useState<string>('');

  const handleChange = (newValue: number): void => {
    setSelectTab(newValue);
  };

  const getErrorMessage = (
    responseMessage: string,
    translate: TFunction<'translation', undefined>
  ): string => {
    if (responseMessage === 'invalid schedule date') {
      return translate('payment_file.invalid_date');
    }
    return responseMessage;
  };

  const handleUploadFile = async (
    file: File | undefined,
    options: UploadOptions
  ): Promise<void> => {
    setLoading(true);
    try {
      if (file) {
        const { isProcessingNow, scheduleDate } = options;
        const formData = new FormData();

        formData.append('file', file);
        formData.append('schedule_date', scheduleDate);
        formData.append('isProcessingNow', `${isProcessingNow}`);

        const response = await uploadRecurringFile(
          formData,
          auth.currentUser.merchantID as string
        );
        if (response?.status.messages[0]) {
          const message = getErrorMessage(response.status.messages[0], t);
          setFailedAlert(message);
        }
      }
      setUploaded(true);
    } catch (error) {
      if (error !== STATUS_CODE_NOT_AUTHORIZED) {
        setUploaded(true);
      }
    } finally {
      setUploaded(false);
      setLoading(false);
    }
  };

  const hasViewAndCheckerPermission = useMemo(() => {
    const permissions = auth.currentUser.permissions;
    const checkerPermissions = [
      PAYMENT_VIEW_PERMISSION,
      PAYMENT_CHECKER_O1_PERMISSION,
      PAYMENT_CHECKER_O2_PERMISSION,
    ];
    const userCheckerPermission = permissions?.find((permission: string) =>
      checkerPermissions.includes(permission)
    );
    return userCheckerPermission;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (failedAlert) {
      const timeout = setInterval(() => {
        clearInterval(timeout);

        setFailedAlert('');
      }, ms('60s'));
    }
  }, [failedAlert]);

  return {
    handleChange,
    handleUploadFile,
    hasViewAndCheckerPermission,
    selectTab,
    uploaded,
    failedAlert,
    auth,
    setLoading,
    uploadRecurringFile,
  };
};

export default useViewModel;
